import { QuoteSheetDTO } from '@/api/procurement/quote-sheets-types';
import { Button } from '@/components/ui/button';
import { useReprocessEmailForQuoteSheet } from '@/api/procurement/useReprocessEmailForQuoteSheet';
import { useUpdateQuoteSheet } from '@/api/procurement/useUpdateQuoteSheet';
import { useQueryClient } from '@tanstack/react-query';
import { DatePicker } from '@/components/common/date-pickers/date-picker';
import { SupplierAutocomplete } from '@/components/company/SupplierAutocomplete';

interface QuoteSheetMetadataProps {
  quoteSheet: QuoteSheetDTO;
}

export function QuoteSheetMetadata({ quoteSheet }: QuoteSheetMetadataProps) {
  const queryClient = useQueryClient();

  const reprocessEmail = useReprocessEmailForQuoteSheet({
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({
          queryKey: [
            `accounts-payable/quote-sheets/${data.quoteSheetId}`,
            undefined,
          ],
        });
      }
    },
  });

  const updateQuoteSheet = useUpdateQuoteSheet({
    quoteSheetId: quoteSheet.id,
  });

  const handleQuoteDateChange = (date: Date | undefined) => {
    if (date) {
      // Format as YYYY-MM-DD without timezone conversion
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;

      updateQuoteSheet.mutate({
        quoteSheetId: quoteSheet.id,
        quoteDate: dateString,
      });
    }
  };

  // Calculate suggestion statistics
  const totalLineItems = quoteSheet.lineItems?.length ?? 0;

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-gray-600">
            Quote Date
          </label>
          <DatePicker
            value={
              quoteSheet.quoteDate
                ? (() => {
                    // Extract date part and create a local date to avoid timezone issues
                    const dateStr = quoteSheet.quoteDate.split('T')[0];
                    const [year, month, day] = dateStr.split('-').map(Number);
                    return new Date(year, month - 1, day);
                  })()
                : undefined
            }
            onSelect={handleQuoteDateChange}
            className="w-full"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-600">Supplier</label>
          <div className="text-sm font-semibold">
            <SupplierAutocomplete
              supplierId={quoteSheet.supplierId}
              onSelect={(supplierId) =>
                updateQuoteSheet.mutate({
                  quoteSheetId: quoteSheet.id,
                  supplierId,
                })
              }
            />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-600">
            Total Line Items
          </label>
          <div className="text-sm font-semibold">{totalLineItems}</div>
        </div>
        {quoteSheet.currency && (
          <div>
            <label className="text-sm font-medium text-gray-600">
              Currency
            </label>
            <div className="text-sm font-semibold">{quoteSheet.currency}</div>
          </div>
        )}
      </div>
      <div className="pt-2 space-y-2">
        {quoteSheet.inboundEmail && (
          <Button
            onClick={() =>
              reprocessEmail.mutate({ quoteSheetId: quoteSheet.id })
            }
            disabled={reprocessEmail.isLoading}
            size="sm"
            variant="outline"
          >
            {reprocessEmail.isLoading
              ? 'Reprocessing…'
              : 'Reprocess Email (full re-ingestion)'}
          </Button>
        )}
      </div>
    </div>
  );
}
